import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { MeasurementUnit } from "@/types/measurements";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function generateQuoteNumber(prefix: string = 'DPL'): string {
  const sequence = Math.floor(Math.random() * 900000) + 100000;
  return `${prefix}${sequence}`;
}

export function generateStandardQuoteNumber(prefix: string = 'EST'): string {
  const now = new Date();
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = months[now.getMonth()];
  const year = now.getFullYear().toString().slice(-2);
  const sequence = String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0');
  return `${prefix}/EST/${month}${year}/${sequence}`;
}

/**
 * Calculate area in square feet from dimensions
 * Business Formula: Area = (L * B) / 900 rounded up (for cm)
 * Supports multiple measurement units
 */
export function calculateArea(
  width: number,
  height: number,
  unit: MeasurementUnit = 'cm'
): number {
  if (!width || !height) return 0;

  // Convert to cm first based on unit
  let widthCm = width;
  let heightCm = height;

  switch (unit) {
    case 'inch':
      widthCm = width * 2.54;
      heightCm = height * 2.54;
      break;
    case 'mm':
      widthCm = width / 10;
      heightCm = height / 10;
      break;
    case 'ft':
      widthCm = width * 30.48;
      heightCm = height * 30.48;
      break;
    case 'cm':
    default:
      // Already in cm
      break;
  }

  // Convert cm² to sqft: (L × B) / 929.0304
  return Number(((widthCm * heightCm) / 929.0304).toFixed(2));
}

/**
 * Calculate total amount based on UOM
 * For Sqft/Rft: multiplier * rate * quantity
 * For Nos/Set/Lot: rate * quantity
 */
export function calculateAmount(
  uom: string,
  rate: number,
  quantity: number,
  sqft: number
): number {
  if (uom === 'Sqft') {
    return sqft * rate * quantity;
  } else if (uom === 'Rft') {
    // For Rft, sqft represents running feet (width only)
    return rate * quantity;
  }
  // Default case for 'Nos', 'Set', 'Lot', etc.
  return rate * quantity;
}

export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for SSR/Legacy
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
